/* tslint:disable:import-spacing */
import {css} from 'aphrodite/no-important';
import {FeatureCollection} from 'geojson';
import React, {MouseEvent as ReactMouseEvent, ReactElement, ReactNode} from 'react';
import MessageAgeInMinutes from '../../../../components/MessageAgeInMinutes';
import OverviewAllRoadWork from '../../../../components/OverviewAllTables/components/OverviewAllRoadWork';
import OverviewAllSituationBridge from '../../../../components/OverviewAllTables/components/OverviewAllSituationBridge';
import OverviewAllSituationOnda from '../../../../components/OverviewAllTables/components/OverviewAllSituationOnda';
import OverviewSituationAllTrafficMessage
    from '../../../../components/OverviewAllTables/components/OverviewAllTrafficMessage';
import OverviewAllWazeAlertIncident
    from '../../../../components/OverviewAllTables/components/OverviewAllWazeAlertIncident';
import OverviewAllWazeAlertOther from '../../../../components/OverviewAllTables/components/OverviewAllWazeAlertOther';
import {wazeTypesOptions} from '../../../../constants';
import ThemeContext from '../../../../contexts/ThemeContext';
import {DATA_SOURCE_STATUS_UPDATE_OUT_OF_SYNC, DataSourceGeoJson} from '../../../../interfaces/DataSource';
import {
    OverviewAllTable,
    OverviewTableSource,
    OverviewTableSourceType
} from '../../../../interfaces/OverviewAllTable';
import {SituationFeature} from '../../../../interfaces/SituationFeature';
import {WazeFeature} from '../../../../interfaces/WazeFeature';
import {filterByTrafficCenter} from '../../../../services/TrafficCenterService';
import {DataSourceState} from '../../reducers/dataReducer';
import OverviewTableSourceButton from './components/OverviewTableSourceButton';
import {Props} from './Props';
import {State} from './State';
import styles, {darkStyles} from './styles';

class OverviewTable extends React.Component<Props, State> {
    static contextType = ThemeContext;
    context!: React.ContextType<typeof ThemeContext>;

    constructor(props: Props) {
        super(props);

        this.state = {
            dragIsEnabled: false,
            features: []
        };
    }

    componentDidMount(): void {
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
        this.updateFeatures();
    }

    componentWillUnmount(): void {
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const {
            currentVisibleTableSource,
            overviewAllData
        } = this.props;

        if (!currentVisibleTableSource || !prevProps.currentVisibleTableSource) {
            return;
        }
        const isEqualSnapshot =
            overviewAllData[currentVisibleTableSource.dataSource]?.lastSnapshotUpdateTimestamp ===
            prevProps.overviewAllData[prevProps.currentVisibleTableSource.dataSource]?.lastSnapshotUpdateTimestamp;
        const filterTimeStampChanged = prevProps.mapFilter.filterTimeStamp !== this.props.mapFilter.filterTimeStamp;
        const isEqualUpdate =
            overviewAllData[currentVisibleTableSource.dataSource]?.lastUpdateReceived ===
            prevProps.overviewAllData[prevProps.currentVisibleTableSource.dataSource]?.lastUpdateReceived;

        if (filterTimeStampChanged || !isEqualSnapshot || !isEqualUpdate) {
            this.updateFeatures();
        }
    }

    async updateFeatures() {
        const {
            currentVisibleTableSource,
            overviewAllData,
        } = this.props;
        if (currentVisibleTableSource) {
            const features = await this.getFeatureTableData(overviewAllData, currentVisibleTableSource) as WazeFeature[];
            this.setState({features});
        }
    }

    onMouseDownDragHandle = (event: ReactMouseEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({
            dragIsEnabled: true,
        });
    };

    onMouseUp = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        if (!this.state.dragIsEnabled) {
            return;
        }

        this.setState({
            dragIsEnabled: false,
        });
    };

    onMouseMove = (event: MouseEvent): void => {
        const {updateOverviewTableHeight} = this.props;
        const {dragIsEnabled} = this.state;

        if (!dragIsEnabled) {
            return;
        }

        updateOverviewTableHeight(event.clientY);
    };

    render(): ReactElement {
        const {
            currentVisibleTable,
            currentVisibleTableSource,
            overviewTablesVisible,
            overviewAllData,
            onOverviewTableChangeOverviewTableSource,
        } = this.props;
        const {dragIsEnabled} = this.state;

        if (!overviewTablesVisible) {
            return <React.Fragment/>;
        }

        return (
            <div className={css(styles.container)}>
                {currentVisibleTable && (
                    <>
                        <ul className={css(styles.sourceContainer)}>
                            {currentVisibleTable.sources.map((source: OverviewTableSource) => (
                                <OverviewTableSourceButton
                                    active={
                                        (currentVisibleTableSource &&
                                            currentVisibleTableSource.sourceType === source.sourceType) ||
                                        false
                                    }
                                    source={source}
                                    onOverviewTableChangeOverviewTableSource={onOverviewTableChangeOverviewTableSource}
                                    key={source.sourceType}
                                />
                            ))}
                        </ul>
                    </>
                )}
                <div
                    className={css(styles.dragHandle, this.context.currentColorSchemeIsDark && darkStyles.dragHandle)}
                    onMouseDown={this.onMouseDownDragHandle}
                    style={{cursor: dragIsEnabled ? 'grabbing' : 'row-resize'}}
                >
                    <div className={css(styles.dragHandleLine)}/>
                    <div
                        className={css(
                            styles.dragHandleDots,
                            this.context.currentColorSchemeIsDark && darkStyles.dragHandleDots,
                        )}
                    >
                        •••
                    </div>
                </div>

                <div className={css(styles.tableContainer)}>
                    {currentVisibleTableSource && this.renderTable(currentVisibleTableSource)}
                </div>

                {this.renderLastSyncTimestamp(overviewAllData, currentVisibleTable, currentVisibleTableSource)}
            </div>
        );
    }

    private renderTable = (
        overviewTableSource: OverviewTableSource,
    ): ReactNode => {
        const {mapFilter, showRecordIds, mapMovementAvailable, clickedOnTableRow, onSaveWazeAlertData} = this.props;
        switch (overviewTableSource.sourceType) {
            case OverviewTableSourceType.WazeAlertIncident: {
                return (
                    <OverviewAllWazeAlertIncident
                        data={this.state.features as WazeFeature[]}
                        wazeDataTypes={wazeTypesOptions}
                        mapFilter={mapFilter}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                        onSaveWazeAlertData={onSaveWazeAlertData}
                    />
                );
            }
            case OverviewTableSourceType.WazeAlertOther: {
                return (
                    <OverviewAllWazeAlertOther
                        data={this.state.features as WazeFeature[]}
                        mapFilter={mapFilter}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                    />
                );
            }
            case OverviewTableSourceType.SituationOnda: {
                return (
                    <OverviewAllSituationOnda
                        data={this.state.features as SituationFeature[]}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                    />
                );
            }
            case OverviewTableSourceType.SituationTrafficMessages: {
                return (
                    <OverviewSituationAllTrafficMessage
                        data={this.state.features as SituationFeature[]}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                    />
                );
            }
            case OverviewTableSourceType.SituationBridge: {
                return (
                    <OverviewAllSituationBridge
                        data={this.state.features as SituationFeature[]}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                    />
                );
            }
            case OverviewTableSourceType.SituationRoadWorkActual: {
                return (
                    <OverviewAllRoadWork
                        data={this.state.features as SituationFeature[]}
                        showRecordIds={showRecordIds}
                        suppressRowClickSelection={!mapMovementAvailable}
                        clickedOnTableRow={(latitude: number, longitude: number) =>
                            clickedOnTableRow(latitude, longitude)
                        }
                    />
                );
            }
            default:
                return <React.Fragment/>;
        }
    };

    private async getFeatureTableData(
        overviewAllData: DataSourceState,
        overviewTableSource: OverviewTableSource
    ) {
        const dataSource = overviewAllData[overviewTableSource.dataSource] as DataSourceGeoJson;
        const features = dataSource?.layerMapData
            ? dataSource.layerMapData.features
            : [];

        const fc: FeatureCollection = {
            type: 'FeatureCollection',
            features: features
        };

        const filteredFc = await filterByTrafficCenter(fc, this.props.mapFilter.currentTrafficCenter);
        return filteredFc.features;
    }

    private renderLastSyncTimestamp = (
        dataSourceState: DataSourceState | null,
        currentVisibleTable: OverviewAllTable | null,
        currentVisibleTableSource: OverviewTableSource | null,
    ): ReactNode => {
        if (currentVisibleTableSource && dataSourceState) {
            const dataSource = dataSourceState[currentVisibleTableSource.dataSource];
            const tableName = `${currentVisibleTable?.name || '-'} / ${currentVisibleTableSource?.name || '-'}`;
            if (
                dataSource &&
                dataSource.status === DATA_SOURCE_STATUS_UPDATE_OUT_OF_SYNC &&
                dataSource.lastUpdateReceived
            ) {
                return (
                    <div
                        className={css(styles.outOfSync, this.context.currentColorSchemeIsDark && darkStyles.outOfSync)}
                    >
                        {`Leeftijd van ${tableName}: `}
                        <MessageAgeInMinutes time={dataSource.lastUpdateReceived} seconds={true}/>
                    </div>
                );
            }
        }

        return <React.Fragment/>;
    };
}

export default OverviewTable;
